<template>
  <div class="b44-page">
    <div class="b44-header">
      <div>
        <h1 class="b44-title">Dashboard de Projetos</h1>
        <p class="b44-sub">Metodologia Roboflex — ritmo e tração do portfólio</p>
        <div class="b44-chips">
          <span class="b44-chip">
            <strong>{{ kpis.total_monitorados || 0 }}</strong>
            <span>projetos ativos monitorados</span>
          </span>
          <button
            v-for="imp in impactos"
            :key="imp.valor"
            type="button"
            class="b44-chip clicavel"
            :class="[imp.tone, { active: impactoFiltro === imp.valor, dimmed: impactoFiltro !== null && impactoFiltro !== imp.valor }]"
            :title="`Ver somente projetos ${imp.label.toLowerCase()}`"
            @click="alternarImpacto(imp.valor)"
          >
            <strong>{{ kpis[imp.kpi] || 0 }}</strong>
            <span>{{ imp.label }}</span>
          </button>
        </div>
      </div>
      <div style="display:flex;flex-direction:column;align-items:flex-end;gap:0.65rem">
        <DocLink from="dashboard" />
        <router-link class="b44-link" :to="{ name: 'portfolioProjetosAuditor', query: queryPortfolio() }">
          <i class="bi bi-folder2-open"></i> Ver portfólio completo
        </router-link>
      </div>
    </div>

    <div v-if="loading" class="b44-loading"><div class="b44-spinner"></div></div>
    <div v-else class="b44-stack" style="margin-top:1.5rem">
      <NotaPortfolioCard :payload="notaPortfolio" />
      <div class="b44-grid-4">
        <div class="b44-metric primary">
          <h3 class="b44-metric-title"><i class="bi bi-currency-dollar"></i> Orçamento × Custo Executado <HelpTip tip="dash_orcamento" /></h3>
          <div class="b44-metric-row"><span class="lbl">Orçamento aprovado</span><span class="val strong">{{ fmtMoeda(resumo.orcamento_total) }}</span></div>
          <div class="b44-metric-row">
            <span class="lbl">Custo executado</span>
            <span class="val" :class="{ danger: resumo.orcamento_total > 0 && resumo.custo_total > resumo.orcamento_total }">{{ fmtMoeda(resumo.custo_total) }}</span>
          </div>
          <p class="b44-metric-foot">{{ resumo.orcamento_total > 0 ? `${resumo.pct_custo}% do orçamento consumido` : "Sem orçamento definido" }}</p>
        </div>

        <div class="b44-metric" :class="(resumo.projetos_atrasados || 0) > 0 ? 'red' : 'green'">
          <h3 class="b44-metric-title"><i class="bi bi-clock"></i> Média de Dias em Atraso dos Projetos Ativos <HelpTip tip="dash_atraso" /></h3>
          <p class="b44-metric-main">{{ Number(resumo.media_dias_atraso || 0).toFixed(1) }}</p>
          <p class="b44-metric-foot">{{ resumo.projetos_atrasados || 0 }} de {{ resumo.projetos_ativos || 0 }} projeto(s) ativo(s) atrasado(s)</p>
        </div>

        <div class="b44-metric blue">
          <h3 class="b44-metric-title"><i class="bi bi-bullseye"></i> Entregas monitoradas <HelpTip tip="dash_resultados" /></h3>
          <div class="b44-metric-row"><span class="lbl">Total monitorado</span><span class="val strong">{{ resumo.total_resultados || 0 }}</span></div>
          <div class="b44-metric-row"><span class="lbl">Validados</span><span class="val">{{ resumo.resultados_validados || 0 }}</span></div>
          <div class="b44-metric-row"><span class="lbl">Atrasados</span><span class="val" :class="{ danger: (resumo.resultados_atrasados || 0) > 0 }">{{ resumo.resultados_atrasados || 0 }}</span></div>
          <div class="b44-metric-row"><span class="lbl">Concluídos</span><span class="val">{{ resumo.resultados_concluidos || 0 }}</span></div>
          <div class="b44-bar"><i :style="{ width: (resumo.pct_resultados || 0) + '%' }"></i></div>
          <p class="b44-metric-foot">{{ resumo.pct_resultados || 0 }}% concluídos</p>
        </div>

        <div class="b44-metric amber">
          <h3 class="b44-metric-title"><i class="bi bi-box-seam"></i> Pacotes de trabalho do portfólio <HelpTip tip="dash_entregas" /></h3>
          <div class="b44-metric-row"><span class="lbl">Total de pacotes</span><span class="val strong">{{ resumo.total_entregas || 0 }}</span></div>
          <div class="b44-metric-row"><span class="lbl">Em atraso</span><span class="val" :class="{ danger: (resumo.entregas_atrasadas || 0) > 0 }">{{ resumo.entregas_atrasadas || 0 }}</span></div>
          <div class="b44-metric-row"><span class="lbl">Concluídos</span><span class="val">{{ resumo.entregas_concluidas || 0 }}</span></div>
          <div class="b44-bar"><i :style="{ width: (resumo.pct_entregas || 0) + '%' }"></i></div>
          <p class="b44-metric-foot">{{ resumo.pct_entregas || 0 }}% concluídos</p>
        </div>
      </div>

      <div class="b44-card p-6">
        <div class="b44-bloco-head">
          <i class="bi bi-kanban" style="color:var(--b44-primary)"></i>
          <h2 class="b44-h2">Matriz de Decisão Roboflex — Projetos <HelpTip tip="dash_matriz" /></h2>
          <router-link class="b44-link spacer" :to="{ name: 'portfolioProjetosAuditor', query: queryPortfolio() }">Ver todos →</router-link>
        </div>
        <div class="b44-grid-6" style="margin-bottom:1.25rem">
          <router-link
            v-for="q in quadrantes"
            :key="q.key"
            class="b44-quad"
            :class="q.tone"
            :to="{ name: 'portfolioProjetosAuditor', query: queryPortfolio({ matriz: q.key }) }"
          >
            <div class="b44-quad-top">
              <i class="b44-quad-icon" :class="q.icon"></i>
              <span class="b44-quad-count" :class="q.tone">{{ matrizCounts[q.key] || 0 }}</span>
            </div>
            <div class="b44-quad-label">{{ q.label }}</div>
            <div class="b44-quad-desc">{{ q.desc }}</div>
          </router-link>
        </div>
        <p v-if="matriz.sem_classificacao > 0" class="b44-metric-foot" style="margin-bottom:0.75rem">
          {{ matriz.sem_classificacao }} projeto(s) ativo(s) sem classificação.
        </p>
        <div v-if="(matriz.top_intervir || []).length" class="b44-list-intervir">
          <p class="b44-xs-muted" style="margin-bottom:0.5rem;font-weight:500">Projetos que exigem intervenção</p>
          <router-link
            v-for="p in matriz.top_intervir"
            :key="p.id"
            :to="{ name: 'portfolioProjetoDetalheAuditor', params: { id: p.id } }"
          >
            <span v-if="p.codigo" class="cod">{{ p.codigo }}</span>
            <span class="nome">{{ p.nome }}</span>
            <span class="score">{{ p.score_planejamento ?? 0 }}×{{ p.score_execucao ?? 0 }}</span>
            <MatrizBadge :classificacao="p.classificacao_matriz" />
          </router-link>
        </div>
      </div>

      <div v-if="temAlertas" class="b44-pontos">
        <div class="b44-pontos-head">
          <i class="bi bi-exclamation-triangle-fill"></i>
          <h2 class="b44-h2">Pontos de Atenção — Atrasos e Estouro de Custo <HelpTip tip="dash_alertas" /></h2>
        </div>
        <div class="b44-alertas">
          <div v-for="a in alertasVisiveis" :key="a.key" class="b44-alerta-card" :class="[a.tone, { dark: a.dark }]">
            <div class="b44-alerta-top">
              <i :class="a.icon"></i>
              <span class="b44-alerta-count">{{ a.count }}</span>
            </div>
            <p class="b44-alerta-label">{{ a.label }}</p>
            <p class="b44-alerta-sub">{{ a.sub }}</p>
            <div class="b44-alerta-items">
              <router-link
                v-for="item in a.items"
                :key="item.id"
                class="b44-alerta-item"
                :to="{ name: 'portfolioProjetoDetalheAuditor', params: { id: item.id } }"
              >
                <p class="nome">
                  <span v-if="item.codigo" class="cod">{{ item.codigo }}</span>{{ item.nome }}
                </p>
                <p class="detail">{{ item.detail }}</p>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import "@/assets/css/portfolio-auditor.css";
import servicePortfolioProjetos from "@/services/servicePortfolioProjetos";
import { getPermissao } from "@/services/permissao-service";
import { fmtMoeda } from "@/utils/roboflexLabels";
import MatrizBadge from "@/components/PortfolioAuditor/MatrizBadge.vue";
import HelpTip from "@/components/PortfolioAuditor/HelpTip.vue";
import DocLink from "@/components/PortfolioAuditor/DocLink.vue";
import NotaPortfolioCard from "@/components/PortfolioAuditor/NotaPortfolioCard.vue";
import { useApiError } from "@/composables/useApiError";

export default {
  name: "DashboardProjetosAuditor",
  components: { MatrizBadge, HelpTip, DocLink, NotaPortfolioCard },
  setup() {
    const { handleError } = useApiError();
    return { handleApiError: handleError, fmtMoeda };
  },
  data() {
    return {
      loading: true,
      kpis: {},
      agregados: {},
      porImpacto: {},
      impactoFiltro: null,
      impactos: [
        { valor: 5, kpi: "estrategicos", label: "Estratégicos", tone: "estrategico" },
        { valor: 3, kpi: "taticos", label: "Táticos", tone: "tatico" },
        { valor: 1, kpi: "operacionais", label: "Operacionais", tone: "operacional" },
      ],
      quadrantes: [
        { key: "intervir", label: "Intervir", desc: "Primeiro da pauta", tone: "red", icon: "bi bi-exclamation-octagon" },
        { key: "agir", label: "Agir", desc: "Monitorar de perto", tone: "amber", icon: "bi bi-lightning-charge" },
        { key: "ideal", label: "Ideal", desc: "Reconhecer desempenho", tone: "green", icon: "bi bi-check-circle" },
        { key: "apoiar", label: "Apoiar", desc: "Melhorar plano e exec.", tone: "blue", icon: "bi bi-shield-check" },
        { key: "questionar", label: "Questionar", desc: "Investigar anomalia", tone: "amber", icon: "bi bi-question-circle" },
        { key: "cancelar", label: "Cancelar", desc: "Candidato a cancelar", tone: "slate", icon: "bi bi-slash-circle" },
      ],
    };
  },
  computed: {
    dados() {
      if (this.impactoFiltro === null) return this.agregados;
      return this.porImpacto[this.impactoFiltro] || {};
    },
    notaPortfolio() {
      return this.dados.nota_portfolio || null;
    },
    resumo() {
      return this.dados.resumo || {};
    },
    matriz() {
      return this.dados.matriz || { counts: {}, top_intervir: [], sem_classificacao: 0 };
    },
    alertas() {
      return this.dados.alertas || {};
    },
    matrizCounts() {
      return this.matriz.counts || {};
    },
    alertasVisiveis() {
      const a = this.alertas || {};
      return [
        {
          key: "projetos_atrasados",
          show: (a.projetos_atrasados?.count || 0) > 0,
          label: "Projetos Atrasados",
          tone: "red",
          icon: "bi bi-clock",
          count: a.projetos_atrasados?.count || 0,
          sub: a.projetos_atrasados?.count ? `Maior atraso: ${a.projetos_atrasados.maior_atraso || 0} dias` : "",
          items: a.projetos_atrasados?.items || [],
        },
        {
          key: "resultados_atrasados",
          show: (a.resultados_atrasados?.count || 0) > 0,
          label: "Entregas atrasadas",
          tone: "rose",
          icon: "bi bi-bullseye",
          count: a.resultados_atrasados?.count || 0,
          sub: `em ${a.resultados_atrasados?.projetos || 0} projeto(s)`,
          items: a.resultados_atrasados?.items || [],
        },
        {
          key: "entregas_atrasadas",
          show: (a.entregas_atrasadas?.count || 0) > 0,
          label: "Pacotes de trabalho atrasados",
          tone: "orange",
          icon: "bi bi-box-seam",
          count: a.entregas_atrasadas?.count || 0,
          sub: `em ${a.entregas_atrasadas?.projetos || 0} projeto(s)`,
          items: a.entregas_atrasadas?.items || [],
        },
        {
          key: "tarefas_atrasadas",
          show: (a.tarefas_atrasadas?.count || 0) > 0,
          label: "Tarefas Atrasadas",
          tone: "amber",
          icon: "bi bi-check2-square",
          count: a.tarefas_atrasadas?.count || 0,
          sub: `em ${a.tarefas_atrasadas?.projetos || 0} projeto(s)`,
          items: a.tarefas_atrasadas?.items || [],
        },
        {
          key: "estouro_custo",
          show: (a.estouro_custo?.count || 0) > 0,
          label: "Estouro de Custo",
          tone: "red",
          dark: true,
          icon: "bi bi-currency-dollar",
          count: a.estouro_custo?.count || 0,
          sub: `Excesso total: ${fmtMoeda(a.estouro_custo?.excesso_total)}`,
          items: a.estouro_custo?.items || [],
        },
      ].filter((x) => x.show);
    },
    temAlertas() {
      return this.alertasVisiveis.length > 0;
    },
  },
  async created() {
    const funcs = await getPermissao();
    if (!funcs.includes(116)) {
      this.$router.replace("/");
      return;
    }
    try {
      const data = await servicePortfolioProjetos.dashboard();
      this.kpis = data.kpis || {};
      this.agregados = {
        nota_portfolio: data.nota_portfolio || null,
        resumo: data.resumo || {},
        matriz: data.matriz || { counts: {}, top_intervir: [], sem_classificacao: 0 },
        alertas: data.alertas || {},
      };
      this.porImpacto = data.por_impacto || {};
    } catch (e) {
      this.handleApiError(e);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    alternarImpacto(valor) {
      this.impactoFiltro = this.impactoFiltro === valor ? null : valor;
    },
    queryPortfolio(extra = {}) {
      return this.impactoFiltro === null ? extra : { ...extra, impacto: String(this.impactoFiltro) };
    },
  },
};
</script>
